import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pause, Play, Sparkles, Square, TimerReset, X } from 'lucide-react';
import backendApi from '../../services/backendApi';
import { announcePomodoroEnd, announcePomodoroStart } from '../../utils/pomodoroAnnouncements';
import './pomodoroControl.css';

const PRESETS = [25, 45, 60];
const formatTime = seconds => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

const getRemaining = pomodoro => {
  if (!pomodoro || pomodoro.status === 'idle') return 0;
  if (pomodoro.status === 'paused') return pomodoro.remaining_seconds || 0;
  return Math.max(0, Math.ceil((new Date(pomodoro.ends_at).getTime() - Date.now()) / 1000));
};

function PomodoroControl() {
  const [duration, setDuration] = useState(25);
  const [pomodoro, setPomodoro] = useState({ status: 'idle' });
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const sync = useCallback(async () => {
    try {
      const state = await backendApi.getPomodoro();
      setPomodoro(state);
      setRemaining(getRemaining(state));
      setError('');
    } catch (syncError) {
      setError('Minuteur indisponible');
    }
  }, []);

  useEffect(() => {
    sync();
    const poller = window.setInterval(sync, 15000);
    return () => window.clearInterval(poller);
  }, [sync]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  useEffect(() => {
    if (pomodoro.status !== 'running') return undefined;
    const tick = () => {
      const value = getRemaining(pomodoro);
      setRemaining(value);
      if (value === 0) {
        announcePomodoroEnd(pomodoro);
        setPomodoro({ status: 'idle' });
      }
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [pomodoro]);

  const runAction = async (action, onSuccess) => {
    try {
      setBusy(true);
      setError('');
      const state = await action();
      setPomodoro(state);
      setRemaining(getRemaining(state));
      window.dispatchEvent(new CustomEvent('pomodoro:changed', { detail: state }));
      onSuccess?.(state);
    } catch (actionError) {
      setError(actionError.message || 'Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const progress = useMemo(() => {
    const total = pomodoro.duration_seconds || duration * 60;
    return total ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0;
  }, [duration, pomodoro.duration_seconds, remaining]);

  const isActive = pomodoro.status === 'running' || pomodoro.status === 'paused';

  return (
    <div className={`focus-dock ${isOpen ? 'is-open' : ''} ${isActive ? 'is-active' : ''}`}>
      {isOpen && (
        <section className="focus-panel" id="dashboard-pomodoro-panel" aria-label="Pomodoro personnel">
          <header className="focus-panel-header">
            <div className="focus-panel-heading">
              <span className="focus-panel-icon"><Sparkles aria-hidden="true" /></span>
              <div><strong>Mon focus</strong><small>{isActive ? (pomodoro.status === 'paused' ? 'Session en pause' : 'Session en cours') : 'Créez une bulle de concentration'}</small></div>
            </div>
            <button type="button" className="focus-close" onClick={() => setIsOpen(false)} aria-label="Fermer le Pomodoro"><X /></button>
          </header>

          {isActive ? (
            <div className="focus-active-body">
              <div className="focus-time" aria-live="off">{formatTime(remaining)}</div>
              <div className="focus-progress" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
              <div className="focus-session-meta"><span>{pomodoro.status === 'paused' ? 'Votre temps est préservé' : 'Ne rien laisser interrompre ce moment'}</span><b>{Math.round(progress)}%</b></div>
              <div className="focus-actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => runAction(pomodoro.status === 'paused' ? backendApi.resumePomodoro.bind(backendApi) : backendApi.pausePomodoro.bind(backendApi))}
                  className="focus-primary"
                >
                  {pomodoro.status === 'paused' ? <Play /> : <Pause />}
                  {pomodoro.status === 'paused' ? 'Reprendre' : 'Mettre en pause'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => runAction(backendApi.stopPomodoro.bind(backendApi), () => announcePomodoroEnd(pomodoro))}
                  className="focus-stop"
                  aria-label="Arrêter le Pomodoro"
                ><Square /></button>
              </div>
            </div>
          ) : (
            <div className="focus-idle-body">
              <div className="focus-presets" aria-label="Durées rapides">
                {PRESETS.map(minutes => (
                  <button type="button" key={minutes} onClick={() => setDuration(minutes)} className={duration === minutes ? 'is-selected' : ''}>
                    <strong>{minutes}</strong><span>min</span>
                  </button>
                ))}
              </div>
              <label className="focus-custom-duration">
                <span>Durée personnalisée</span>
                <div><input type="number" aria-label="Durée personnalisée en minutes" min="1" max="180" value={duration} onChange={event => setDuration(Number(event.target.value))} /><small>minutes</small></div>
              </label>
              <button
                type="button"
                disabled={busy || duration < 1 || duration > 180}
                onClick={() => runAction(() => backendApi.startPomodoro(duration), announcePomodoroStart)}
                className="focus-start"
              ><Play /> Démarrer {duration > 0 ? `${duration} min` : ''}</button>
            </div>
          )}
          <div className="focus-error" aria-live="polite">{error}</div>
        </section>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(value => !value)}
        className="focus-trigger"
        style={{ '--focus-progress': `${progress * 3.6}deg` }}
        aria-expanded={isOpen}
        aria-controls="dashboard-pomodoro-panel"
        title={isActive ? `Pomodoro ${formatTime(remaining)}` : 'Démarrer un Pomodoro'}
        aria-label={isActive ? `Pomodoro, ${formatTime(remaining)} restantes` : 'Ouvrir le Pomodoro'}
      >
        <span className="focus-trigger-face"><TimerReset /></span>
        {isActive && <strong>{formatTime(remaining)}</strong>}
      </button>
    </div>
  );
}

export default PomodoroControl;
